import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect } from 'react';

import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { WhalePositionEvent } from '@flipster/utils/segment/events/whale-position';

import BigWhaleImage from './big-whale.webp';
import { SymbolScope, whalePositionBunja } from './bunja';
import { WhaleDetails } from './whale-details';
import { WhaleHeader } from './whale-header';
import WhaleImage from './whale.webp';

const bigWhaleThreshold = 1000000;

export interface WhaleGraphicMeta {
  image: string;
  text: string;
  width: number;
  height: number;
  leftMargin: number;
  rightMargin: number;
}

const bigWhaleGraphicMeta: WhaleGraphicMeta = {
  image: BigWhaleImage,
  text: 'Big Whale',
  width: 42,
  height: 42,
  leftMargin: -6,
  rightMargin: 3,
};

const normalWhaleGraphicMeta: WhaleGraphicMeta = {
  image: WhaleImage,
  text: 'Whale',
  width: 20,
  height: 28,
  leftMargin: -1,
  rightMargin: 5,
};

export const WhalePositionCard = ({
  symbol,
  quoteCurrency,
  onCopyClick,
  symbolPricePrecision,
}: {
  symbol: string;
  quoteCurrency: string;
  onCopyClick: (entryPrice: string) => void;
  symbolPricePrecision: number;
}) => {
  const { currentWhalePositionAtom, resetWhalePositions } = useBunja(whalePositionBunja, [SymbolScope.bind(symbol)]);
  const { isMobile } = useBreakpoint();
  const whalePosition = useAtomValue(currentWhalePositionAtom);

  const shouldShow = whalePosition && !isMobile;
  const handleClickCopyEntryPrice = () => {
    if (!whalePosition?.entryPrice) return;
    segment.track(WhalePositionEvent.WhaleCardClick, {
      position_size_usdt: whalePosition.positionSize,
      symbol,
      position: whalePosition.side,
      whale_type: isBigWhale ? 'Big' : 'Regular',
    });
    onCopyClick(whalePosition.entryPrice);
  };

  const isBigWhale = Number(whalePosition?.positionSize || 0) >= bigWhaleThreshold;
  const whaleGraphicMeta = isBigWhale ? bigWhaleGraphicMeta : normalWhaleGraphicMeta;

  useEffect(() => {
    resetWhalePositions();
  }, [resetWhalePositions, symbol]);

  useEffect(() => {
    if (!whalePosition) return;

    segment.track(WhalePositionEvent.WhaleCardView, {
      position_size_usdt: whalePosition.positionSize,
      symbol,
      position: whalePosition.side,
      whale_type: isBigWhale ? 'Big' : 'Regular',
    });
  }, [isBigWhale, symbol, whalePosition]);
  return (
    <AnimatePresence mode='wait'>
      {shouldShow && (
        <m.div
          key={`${whalePosition?.side}-${whalePosition?.entryPrice}-${whalePosition?.positionSize}`}
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.5 },
          }}
          style={{
            zIndex: 1,
            position: 'absolute',
            right: 'min(25%, 200px)',
            top: '80px',
            overflow: 'visible',
          }}
        >
          <div className={css({ pos: 'relative' })}>
            <div
              className={stack({
                gap: 8,
                w: 180,
                h: 94,
                p: 8,
                border: '1px solid',
                borderColor: 'border.default.level-1',
                rounded: 8,
                transition: 'opacity 0.2s ease-in-out',
                bgColor: 'background.default.level-1',
              })}
            >
              <WhaleHeader whaleGraphicMeta={whaleGraphicMeta} whalePosition={whalePosition} />
              <WhaleDetails
                whalePosition={whalePosition}
                quoteCurrency={quoteCurrency}
                symbolPricePrecision={symbolPricePrecision}
                onCopyClick={handleClickCopyEntryPrice}
              />
            </div>
            <button
              className={css({ pos: 'absolute', top: -8, right: -8, color: 'content.default.level-3' })}
              onClick={resetWhalePositions}
            >
              <XCircleIcon size={16} />
            </button>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};
